import { useEffect, useState } from "react";
import { useString as s } from "../../../../components/StringProvider";
import ConnectionLayout from "../../../../components/Layout/ConnectionLayout";
import HubspotConnectionForm from "./HubspotConnectionForm";
import DisconnectModal from "../DisconnectModal";
import { bindActionCreators, compose } from "redux";
import { connect } from "react-redux";
import {
  selectRequestData,
  selectRequestState
} from "../../../../store/reducers";
import {
  createIntegration,
  updateIntegration,
  getIntegration,
  deleteIntegration
} from "../../../../store/actions/integrations";
import types from "../../../../store/actionTypes";
import IntegrationNames from "../../../../utils/constants/integration-names";
import useLoadingState from "../../../../utils/use-loading-state";
import { message } from "antd";

const HubspotConnection = ({
  createIntegrationLoadingState,
  deleteIntegrationLoadingState,
  updateIntegrationLoadingState,
  createIntegration,
  updateIntegration,
  integrationData,
  getIntegration,
  deleteIntegration,
  createIntegrationData,
  updateIntegrationData
}) => {
  const header = s(
    "generalAdmin.page.hubspotConnection.header",
    "Enter the connection details you generated in HubSpot to activate the connection between Cuvama and HubSpot."
  );
  const disconnectHeader = s(
    "disconnectHubspot.popup.header",
    "Disconnect HubSpot"
  );
  const disconnectSubheader = s(
    "disconnectHubspot.popup.subheader",
    "Do you want to disconnect from HubSpot and delete your settings?"
  );
  const createIntegrationError = s(
    "generalAdmin.page.hubspotConnection.messages.createIntegrationError",
    "Failed to fetch connection information"
  );
  const updateIntegrationError = s(
    "generalAdmin.page.hubspotConnection.messages.updateIntegrationError",
    "Failed to update connection information"
  );

  const [editing, setEditing] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const isConnected =
    integrationData?.clientSecret && integrationData?.accessToken;

  useEffect(() => {
    getIntegration({ name: IntegrationNames.HUBSPOT });
  }, []);

  useEffect(() => {
    setEditing(!integrationData);
  }, [integrationData]);

  useLoadingState(
    createIntegrationLoadingState,
    () => {
      handleOAuthRedirect(createIntegrationData);
    },
    () => {
      message.error(createIntegrationError);
    }
  );

  useLoadingState(
    updateIntegrationLoadingState,
    () => {
      handleOAuthRedirect(updateIntegrationData);
    },
    () => {
      message.error(updateIntegrationError);
    }
  );

  const onConnect = ({ baseUrl, clientId, clientSecret }) => {
    createIntegration({
      name: IntegrationNames.HUBSPOT,
      baseUrl,
      clientId,
      clientSecret
    });
  };

  const onUpdate = ({ baseUrl, clientId, clientSecret }) => {
    updateIntegration({
      name: IntegrationNames.HUBSPOT,
      changes: { baseUrl, clientId, clientSecret }
    });
  };

  const handleOAuthRedirect = (data) => {
    if (data) {
      const { baseUrl, clientId, name } = data;

      const url = new URL(`${baseUrl}/oauth/authorize`);

      url.searchParams.append("client_id", clientId);
      url.searchParams.append(
        "scope",
        "crm.objects.deals.read crm.objects.deals.write"
      );
      url.searchParams.append(
        "redirect_uri",
        `${window.location.origin}/integrations/${name}/callback`
      );

      window.location.href = url.toString();
    }
  };

  const onEdit = () => {
    setEditing(true);
  };

  const onCancelEdit = () => {
    setEditing(false);
  };

  const onDisconnect = () => {
    setDeleteModalVisible(true);
  };

  const onDelete = () => {
    deleteIntegration({ name: IntegrationNames.HUBSPOT });
  };

  return (
    <ConnectionLayout header={header} isConnected={isConnected}>
      <HubspotConnectionForm
        integrationData={integrationData}
        onConnect={onConnect}
        onCancel={onCancelEdit}
        onDisconnect={onDisconnect}
        onEdit={onEdit}
        onUpdate={onUpdate}
        readOnly={!editing}
        isConnected={isConnected}
      />
      {deleteModalVisible && (
        <DisconnectModal
          onClose={() => setDeleteModalVisible(false)}
          deleteLoadingState={deleteIntegrationLoadingState}
          header={disconnectHeader}
          subHeader={disconnectSubheader}
          handleDelete={onDelete}
        />
      )}
    </ConnectionLayout>
  );
};

const mapStateToProps = (state) => ({
  createIntegrationLoadingState: selectRequestState(
    state,
    types.CREATE_INTEGRATION_REQUEST
  ),
  deleteIntegrationLoadingState: selectRequestState(
    state,
    types.DELETE_INTEGRATION_REQUEST
  ),
  updateIntegrationLoadingState: selectRequestState(
    state,
    types.UPDATE_INTEGRATION_REQUEST
  ),
  integrationData: selectRequestData(state, types.GET_INTEGRATION_REQUEST),
  createIntegrationData: selectRequestData(
    state,
    types.CREATE_INTEGRATION_REQUEST
  ),
  updateIntegrationData: selectRequestData(
    state,
    types.UPDATE_INTEGRATION_REQUEST
  )
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { createIntegration, updateIntegration, getIntegration, deleteIntegration },
    dispatch
  );

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  HubspotConnection
);
